import { useState, useEffect, useCallback, Fragment } from 'react';
import { toast } from 'react-toastify';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';

import {
  Box,
  Button,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Custom Components
import { Spinner, SearchBox, ClientDrawer, BrowserTitle } from '../components';

// Utils
import messages from '../static/messages';
import { formatPhone } from '../utils/formatter';
import { nullClearObject, emptyStringClear } from '../utils/cleaners';
// Services
import clientsServices from '../services/clientsServices';

// Hooks
import useSearch from '../hooks/useSearch';
import usePagination from '../hooks/usePagination';
import useDrawer from '../hooks/useDrawer';

// Styles
import {
  table_header_with_search,
  table_container_base,
  actions_column,
} from '../static/styles';

import { button } from '../static/theme-styles';

// External variables
const base_client = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
};

// Mobile Client Card Component
const MobileClientCard = ({ client, onEdit, onRemove, page, rowsPerPage }) => {
  return (
    <Card 
      variant="outlined" 
      sx={{ 
        mb: 2, 
        bgcolor: '#fff',
        borderColor: '#1976d2',
        borderWidth: 2,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header with Name and Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <NavLink
              to={`/clients/${client.id}`}
              state={{ page, rowsPerPage }}
              style={{ textDecoration: 'none' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#1976d2',
                  }}
                >
                  {client.fullName || 'No Name'}
                </Typography>
                <ChevronRightIcon sx={{ color: '#999', fontSize: 18 }} />
              </Box>
            </NavLink>
          </Box>
          
          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton 
              size="small" 
              onClick={() => onEdit(client)}
              sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
            >
              <EditIcon sx={{ fontSize: 18, color: '#1976d2' }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => onRemove(client.id)}
              sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}
            >
              <DeleteForeverIcon sx={{ fontSize: 18, color: '#f44336' }} />
            </IconButton>
          </Box>
        </Box>

        {/* Company */}
        {client.companyName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BusinessIcon sx={{ fontSize: 16, color: '#757575' }} />
            <Typography variant="body2" sx={{ color: '#555' }}>
              {client.companyName}
            </Typography>
          </Box>
        )}

        {/* Contact Info */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
          {/* Phone - clickable */}
          {client.phoneNumber && (
            <Box 
              component="a" 
              href={`tel:${client.phoneNumber}`}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                textDecoration: 'none',
                color: '#1976d2',
                bgcolor: '#e3f2fd',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                '&:hover': { bgcolor: '#bbdefb' },
              }}
            >
              <PhoneIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatPhone(client.phoneNumber)}
              </Typography>
            </Box>
          )}
          
          {/* Email - clickable */}
          {client.email && (
            <Box 
              component="a" 
              href={`mailto:${client.email}`}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                textDecoration: 'none',
                color: '#1565c0',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <EmailIcon sx={{ fontSize: 16, color: '#757575' }} />
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {client.email}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const Clients = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [clientsList, setClientsList] = useState(null);
  const [totalCount, setTotalCount] = useState();
  const [currentClient, setCurrentClient] = useState(null);
  const [isNewClient, setClientMode] = useState(false);

  const { searchQuery, setSearchQuery, isSearching, searchRequest } = useSearch(clientsServices.searchRequest);
  const { rowsPerPage, setRowsPerPage, page, setPage, handleChangePage, handleChangeRowsPerPage } = usePagination();
  const { drawer, openDrawer, foolCheck } = useDrawer(currentClient);

  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();

  // Action functions
  const onCloseDrawer = async (resetErrors) => {
    const confirmed = await foolCheck();

    if (confirmed === true) {
      setCurrentClient(null);
      setClientMode(false);
      resetErrors && resetErrors();
    }
  };

  const onEditClient = (client) => {
    delete client.createdAt;
    delete client.deletedAt;
    delete client.updatedAt;
    const cleanObject = nullClearObject(client);
    setCurrentClient(cleanObject);
    openDrawer();
  };

  const onAddClient = () => {
    setClientMode(true);
    setCurrentClient(base_client);
    openDrawer();
  };

  // Handler functions
  const handleRemove = (id) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('Client'),
    })
      .then(() => removeClient(id))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Async functions
  const getClientsList = async (skip, take) => {
    try {
      const response = await clientsServices.getAllClients(skip, take, false);
      setTotalCount(response.data.total);
      setClientsList(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const getSearchResult = useCallback(
    async (skip, take) => {
      const result = await searchRequest(skip, take);
      setTotalCount(result.total);
      setClientsList(result.data);
    },
    [searchRequest],
  );

  const removeClient = async (id) => {
    try {
      const response = await clientsServices.remove(id);

      if (response.status === 200) {
        toast.success(messages.client.remove);
        getClientsList(page * rowsPerPage, rowsPerPage);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || messages.errors.error_try_again;
      toast.error(msg);
      console.log(error);
    }
  };

  const createClient = async () => {
    delete currentClient.isEdited;
    emptyStringClear(currentClient);

    try {
      const response = await clientsServices.create(currentClient);

      if (response.status === 201) {
        onCloseDrawer();
        toast.success(messages.client.create);
        navigate(`/clients/${response.data.id}`);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || messages.errors.error_try_again;
      toast.error(msg);
      console.log(error);
    }
  };

  const updateClient = async () => {
    delete currentClient.isEdited;
    emptyStringClear(currentClient);

    try {
      const response = await clientsServices.update(currentClient.id, currentClient);

      if (response.status === 200) {
        onCloseDrawer();
        toast.success(messages.client.update);
        getClientsList(page * rowsPerPage, rowsPerPage);
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    searchQuery === ''
      ? getClientsList(page * rowsPerPage, rowsPerPage)
      : getSearchResult(page * rowsPerPage, rowsPerPage);
  }, [searchQuery, getSearchResult, page, rowsPerPage]);

  useEffect(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      const page = params.get('page');
      const rowsPerPage = params.get('per_page');

      setPage(Number(page));
      setRowsPerPage(Number(rowsPerPage));
    }
  }, []);

  // Check for data loading
  if (!clientsList) return <Spinner />;

  return (
    <Box component="section">
      <BrowserTitle title="Clients | Airmaxx Pro" />
      
      {/* Header - responsive */}
      <Box sx={{ 
        ...table_header_with_search,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0,
        alignItems: isMobile ? 'stretch' : 'center',
      }}>
        <Button 
          variant="contained" 
          endIcon={<AddIcon />} 
          onClick={onAddClient} 
          sx={{ 
            ...button('primary', 'secondary'),
            width: isMobile ? '100%' : 'auto',
          }}
        >
          Add New Client
        </Button>
      </Box>

      {/* Search Box */}
      <Paper sx={{ mb: 2 }}>
        <SearchBox searchRequest={searchRequest} value={searchQuery} setValue={setSearchQuery} />
      </Paper>

      {/* Mobile Summary */}
      {isMobile && (
        <Box sx={{ mb: 2, px: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {totalCount || 0} clients found
          </Typography>
        </Box>
      )}

      {/* Desktop Table View */}
      {!isMobile && (
        <Paper>
          <TableContainer sx={table_container_base}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="20%">Company</TableCell>
                  <TableCell width="30%">Name</TableCell>
                  <TableCell width="20%">Phone</TableCell>
                  <TableCell width="25%">Email</TableCell>
                  <TableCell width="5%">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isSearching ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Spinner />
                    </TableCell>
                  </TableRow>
                ) : (
                  <Fragment>
                    {clientsList.length ? (
                      clientsList.map((client) => (
                        <TableRow key={client.id} hover>
                          <TableCell scope="row">
                            <NavLink
                              className="full-col-flex-link"
                              to={`/clients/${client.id}`}
                              state={{ page, rowsPerPage }}
                            >
                              {client.companyName}
                            </NavLink>
                          </TableCell>
                          <TableCell>
                            <NavLink
                              className="full-col-flex-link"
                              to={`/clients/${client.id}`}
                              state={{ page, rowsPerPage }}
                            >
                              {client.fullName}
                            </NavLink>
                          </TableCell>
                          <TableCell>
                            <NavLink
                              className="full-col-flex-link"
                              to={`/clients/${client.id}`}
                              state={{ page, rowsPerPage }}
                            >
                              {client.phoneNumber && formatPhone(client.phoneNumber)}
                            </NavLink>
                          </TableCell>
                          <TableCell>
                            <NavLink
                              className="full-col-flex-link"
                              to={`/clients/${client.id}`}
                              state={{ page, rowsPerPage }}
                            >
                              {client.email}
                            </NavLink>
                          </TableCell>
                          <TableCell sx={actions_column}>
                            <Fragment>
                              <Tooltip title="Edit Client">
                                <IconButton onClick={() => onEditClient(client)}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Client">
                                <IconButton onClick={() => handleRemove(client.id)}>
                                  <DeleteForeverIcon />
                                </IconButton>
                              </Tooltip>
                            </Fragment>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                          <strong>No Data</strong>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <Box>
          {isSearching ? (
            <Spinner />
          ) : clientsList.length ? (
            clientsList.map((client) => (
              <MobileClientCard
                key={client.id}
                client={client}
                onEdit={onEditClient}
                onRemove={handleRemove}
                page={page}
                rowsPerPage={rowsPerPage}
              />
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No clients found
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Pagination - Desktop */}
      {!isMobile && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Clients per page"
        />
      )}

      {/* Pagination - Mobile */}
      {isMobile && totalCount > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: 2,
          mt: 3,
          mb: 2,
        }}>
          <Button 
            variant="outlined" 
            onClick={() => handleChangePage(null, page - 1)} 
            disabled={page === 0}
            size="small"
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {page + 1} of {Math.ceil(totalCount / rowsPerPage)}
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => handleChangePage(null, page + 1)} 
            disabled={(page + 1) * rowsPerPage >= totalCount}
            size="small"
          >
            Next
          </Button>
        </Box>
      )}

      <ClientDrawer
        drawer={drawer}
        onCloseDrawer={onCloseDrawer}
        currentClient={currentClient}
        setCurrentClient={setCurrentClient}
        isNewClient={isNewClient}
        createClient={createClient}
        updateClient={updateClient}
      />
    </Box>
  );
};

export default Clients;
